<template>
  <div class="ai-login-backdrop" aria-hidden="true">
    <div class="light-beam beam-left"></div>
    <div class="light-beam beam-right"></div>
    <div class="ambient-haze haze-top"></div>
    <div class="ambient-haze haze-bottom"></div>
    <div class="hud-panel hud-panel--left"></div>
    <div class="hud-panel hud-panel--right"></div>
    <div class="energy-floor"></div>
    <div class="energy-floor energy-floor--inner"></div>
    <div class="grid-mask"></div>
    <div class="floating-particles">
      <span
        v-for="particle in particles"
        :key="particle.id"
        class="particle"
        :style="particle.style"
      ></span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

interface ParticleRecord {
  id: number;
  style: Record<string, string>;
}

const particles: ParticleRecord[] = Array.from({ length: 40 }, (_, index) => ({
  id: index,
  style: {
    left: `${(index * 17 + 9) % 100}%`,
    top: `${(index * 23 + 7) % 100}%`,
    width: `${4 + (index % 4) * 3}px`,
    height: `${4 + (index % 4) * 3}px`,
    animationDelay: `${(index % 9) * 0.6}s`,
    animationDuration: `${7 + (index % 6)}s`,
  },
}));

export default defineComponent({
  name: 'AiLoginBackdrop',
  setup() {
    return {
      particles,
    };
  },
});
</script>

<style scoped>
.ai-login-backdrop,
.light-beam,
.ambient-haze,
.hud-panel,
.energy-floor,
.grid-mask,
.floating-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.ai-login-backdrop {
  display: none;
}

.light-beam {
  opacity: 0.92;
  mix-blend-mode: screen;
  filter: blur(10px);
}

.beam-left {
  inset: -12% auto -8% -12%;
  width: 60%;
  background: linear-gradient(
    132deg,
    transparent 34%,
    rgba(255, 255, 255, 0.95) 42%,
    rgba(152, 194, 255, 0.24) 52%,
    transparent 66%
  );
  animation: beamDriftLeft 18s linear infinite;
}

.beam-right {
  inset: -14% -10% -10% auto;
  width: 58%;
  background: linear-gradient(
    220deg,
    transparent 30%,
    rgba(255, 255, 255, 0.95) 42%,
    rgba(145, 210, 255, 0.22) 52%,
    transparent 66%
  );
  animation: beamDriftRight 22s linear infinite;
}

.ambient-haze {
  filter: blur(22px);
}

.haze-top {
  background:
    radial-gradient(
      circle at 50% 14%,
      rgba(255, 255, 255, 0.96),
      rgba(255, 255, 255, 0.18) 34%,
      transparent 62%
    ),
    radial-gradient(
      circle at 50% 30%,
      rgba(166, 208, 255, 0.2),
      transparent 26%
    );
}

.haze-bottom {
  background:
    radial-gradient(
      circle at 50% 100%,
      rgba(119, 172, 255, 0.32),
      transparent 22%
    ),
    radial-gradient(
      circle at 50% 86%,
      rgba(255, 255, 255, 0.46),
      transparent 22%
    );
}

.hud-panel {
  opacity: 0.58;
}

.hud-panel--left {
  inset: 18% auto 18% 2%;
  width: 18%;
  border: 1px solid rgba(174, 213, 255, 0.48);
  border-radius: 28px;
  box-shadow:
    0 0 24px rgba(145, 191, 255, 0.16),
    inset 0 0 0 1px rgba(255, 255, 255, 0.28);
}

.hud-panel--left::before,
.hud-panel--left::after,
.hud-panel--right::before,
.hud-panel--right::after {
  content: '';
  position: absolute;
  background: rgba(151, 196, 255, 0.7);
  box-shadow: 0 0 16px rgba(129, 191, 255, 0.5);
}

.hud-panel--left::before {
  top: 18%;
  right: -10px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.hud-panel--left::after {
  bottom: 12%;
  left: 18px;
  width: calc(100% - 36px);
  height: 1px;
}

.hud-panel--right {
  inset: 24% 2% 24% auto;
  width: 20%;
  border-right: 1px solid rgba(173, 211, 255, 0.54);
  border-top: 1px solid rgba(173, 211, 255, 0.4);
  border-bottom: 1px solid rgba(173, 211, 255, 0.4);
  clip-path: polygon(16% 0, 100% 0, 100% 100%, 16% 100%, 0 84%, 0 16%);
}

.hud-panel--right::before {
  top: 14%;
  left: 12%;
  width: 68%;
  height: 1px;
}

.hud-panel--right::after {
  bottom: 18%;
  left: 24%;
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.grid-mask {
  background-image:
    linear-gradient(rgba(144, 184, 244, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(144, 184, 244, 0.05) 1px, transparent 1px);
  background-size: 62px 62px;
  mask-image: radial-gradient(circle at center, black 34%, transparent 78%);
  opacity: 0.64;
  animation: gridShift 26s linear infinite;
}

.energy-floor {
  inset: auto 50% -110px;
  width: min(92vw, 1100px);
  height: 260px;
  transform: translateX(-50%);
  border-radius: 50%;
  border: 2px solid rgba(200, 228, 255, 0.85);
  box-shadow:
    0 0 0 18px rgba(214, 235, 255, 0.22),
    0 0 0 42px rgba(184, 217, 255, 0.08),
    0 0 50px rgba(132, 177, 255, 0.28);
}

.energy-floor--inner {
  bottom: -78px;
  width: min(72vw, 760px);
  height: 180px;
  border-width: 1px;
  opacity: 0.78;
}

.particle {
  position: absolute;
  border-radius: 999px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(114, 216, 255, 0.8) 45%,
    rgba(114, 216, 255, 0) 100%
  );
  box-shadow: 0 0 18px rgba(114, 216, 255, 0.4);
  animation: particleFloat ease-in-out infinite;
}

@keyframes beamDriftLeft {
  0% {
    transform: translateX(-4%) translateY(-2%) rotate(0deg);
  }
  100% {
    transform: translateX(7%) translateY(2%) rotate(2deg);
  }
}

@keyframes beamDriftRight {
  0% {
    transform: translateX(4%) translateY(0) rotate(0deg);
  }
  100% {
    transform: translateX(-6%) translateY(2%) rotate(-2deg);
  }
}

@keyframes gridShift {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(62px, 62px, 0);
  }
}

@keyframes particleFloat {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(0.72);
    opacity: 0.32;
  }
  50% {
    transform: translate3d(0, -20px, 0) scale(1);
    opacity: 0.96;
  }
}

@media (max-width: 768px) {
  .hud-panel--left,
  .hud-panel--right {
    display: none;
  }

  .energy-floor {
    bottom: -74px;
    height: 180px;
  }
}

@media (max-width: 480px) {
  .beam-left,
  .beam-right {
    opacity: 0.7;
  }
}
</style>
